import { useState, useMemo } from "react";
import { num, toM, ml, money, UNITS, CalcLayout, FormCol, Card, Field, Sel, HowTo, Advanced, AreaTable, sumPerim, ResultCard, useCatalog, ProductSelect } from "./shared";

const TIPO_DESP = { "Recta sencilla": 5, "Con puertas": 8, "Muchas esquinas": 10, "Con patrón": 12, "Diagonal": 15, "Escalera": 12, "Curvas": 18, "Boiserie": 12 };

export default function Molduras({ onSave, s }) {
  const items = useCatalog("molduras");
  const [pid, setPid] = useState("");
  const prod = items.find((p) => p.id === pid);
  const [u, setU] = useState("m");
  const [tipo, setTipo] = useState("Zoclo");
  const [instal, setInstal] = useState("Con puertas");
  const [spaces, setSpaces] = useState([{ nombre: "Habitación 1", largo: "5", ancho: "4", cant: "1" }]);
  const [puertas, setPuertas] = useState("2");
  const [anchoPuerta, setAnchoPuerta] = useState("0.90");
  const [barraLargo, setBarraLargo] = useState("2.40");
  const [despMode, setDespMode] = useState("auto");
  const [despManual, setDespManual] = useState("8");
  const [sepClavos, setSepClavos] = useState("30");
  const [adhRend, setAdhRend] = useState("15");
  const [precioBarra, setPrecioBarra] = useState("");
  const [precioInst, setPrecioInst] = useState("");
  const despPct = despMode === "auto" ? (TIPO_DESP[instal] ?? 8) : num(despManual);
  const pr = prod ? prod.price : num(precioBarra);

  const r = useMemo(() => {
    const perimBruto = sumPerim(spaces, u);
    const descPuertas = Math.max(0, Math.round(num(puertas)) || 0) * toM(anchoPuerta, u);
    const perimNeto = Math.max(0, perimBruto - descPuertas);
    const mlAjust = perimNeto * (1 + despPct / 100);
    const barras = num(barraLargo) > 0 ? Math.ceil(mlAjust / num(barraLargo)) : 0;
    const clavos = num(sepClavos) > 0 ? Math.ceil(perimNeto / (num(sepClavos) / 100)) : 0;
    const adhesivo = num(adhRend) > 0 ? Math.ceil(perimNeto / num(adhRend)) : 0;
    const cMat = barras * pr;
    const total = cMat + num(precioInst);
    const w = [];
    if (perimNeto === 0) w.push("Captura al menos un espacio con medidas.");
    if (instal === "Curvas") w.push("Instalación en curvas: si la moldura es rígida, verifica factibilidad; puede requerir cortes especiales.");
    if (prod && prod.stock < barras) w.push(`Inventario insuficiente de "${prod.name}": stock ${prod.stock}, requieres ${barras} barras.`);
    const rows = [
      { section: "Proyecto" },
      { label: "Producto", value: tipo }, { label: "Instalación", value: instal }, { label: "Espacios", value: `${spaces.length}` },
      { section: "Cálculo lineal" },
      { label: "Perímetro bruto", value: ml(perimBruto) }, { label: "Descuento (puertas)", value: ml(descPuertas) },
      { label: "Perímetro neto", value: ml(perimNeto) }, { label: "Desperdicio aplicado", value: `${despPct}%` },
      { label: "Metros lineales ajustados", value: ml(mlAjust) },
      { label: "Barras a vender", value: `${barras} barra(s) de ${num(barraLargo)} m`, strong: true },
      { section: "Fijación / complementos" },
      { label: "Clavos / tornillos", value: `${clavos}` }, { label: "Adhesivo (unidades)", value: `${adhesivo}` },
      { label: "Terminaciones / esquineros", value: "Según geometría (inglete / accesorio)" },
      { label: "Sellador / resanador", value: "Según acabado y juntas" },
      { section: "Costos" },
      { label: "Materiales (barras)", value: money(cMat) }, { label: "Instalación", value: money(num(precioInst)) },
      { label: "Total estimado", value: money(total), strong: true },
    ];
    return { rows, warnings: w, total, inputs: { u, tipo, instal } };
  }, [u, tipo, instal, spaces, puertas, anchoPuerta, barraLargo, despPct, sepClavos, adhRend, pr, precioInst, prod]);

  return (
    <CalcLayout>
      <FormCol>
        <HowTo title="¿Cómo calcular molduras, zócalos y perfiles?"><p>Se calcula el perímetro de cada espacio, se descuentan puertas/vanos, se aplica el desperdicio y se convierten los metros lineales en barras comerciales (redondeo hacia arriba), más fijación y terminaciones.</p></HowTo>
        <Card>
          <Sel label="Unidad" value={u} onChange={setU} opts={UNITS} testid="mo-unidad" />
          <Sel label="Tipo de producto" value={tipo} onChange={setTipo} opts={["Zoclo", "Moldura de techo", "Perfil de transición", "Perfil de escalera", "Nariz de escalón", "Boiserie", "Perfil en T", "Perfil en L", "Remate"]} testid="mo-tipo" />
          <Sel label="Instalación" value={instal} onChange={setInstal} opts={Object.keys(TIPO_DESP)} testid="mo-instal" />
          <Field label="Largo comercial de barra (m)" value={barraLargo} onChange={setBarraLargo} step="0.1" suffix="m" testid="mo-barralargo" />
        </Card>
        <ProductSelect items={items} value={pid} onChange={setPid} testid="mo-product" />
        <AreaTable title="Espacios (largo × ancho)" rows={spaces} setRows={setSpaces} u={u} defaultName="Habitación" testid="mo-spaces" />
        <Card>
          <Field label="Número de puertas/vanos" value={puertas} onChange={setPuertas} step="1" testid="mo-puertas" />
          <Field label="Ancho por puerta" value={anchoPuerta} onChange={setAnchoPuerta} suffix={u} testid="mo-anchopuerta" />
        </Card>
        <Advanced>
          <Sel label="Desperdicio" value={despMode} onChange={setDespMode} opts={[["auto", `Automático (${TIPO_DESP[instal] ?? 8}%)`], ["manual", "Manual"]]} testid="mo-despmode" />
          {despMode === "manual" && <Field label="Desperdicio (%)" value={despManual} onChange={setDespManual} step="1" suffix="%" testid="mo-despmanual" />}
          <Field label="Separación de clavos (cm)" value={sepClavos} onChange={setSepClavos} step="5" suffix="cm" testid="mo-sepclavos" />
          <Field label="Rendimiento adhesivo (m/cartucho)" value={adhRend} onChange={setAdhRend} step="1" suffix="m" testid="mo-adhrend" />
          <Field label="Precio por barra ($)" value={prod ? prod.price : precioBarra} onChange={setPrecioBarra} suffix="$" testid="mo-precio" />
          <Field label="Instalación ($)" value={precioInst} onChange={setPrecioInst} suffix="$" testid="mo-precio-inst" />
        </Advanced>
      </FormCol>
      <ResultCard title="Cotización de Molduras/Perfiles — GOÛT" modality="molduras" rows={r.rows} warnings={r.warnings} total={r.total} inputs={r.inputs} onSave={onSave} />
    </CalcLayout>
  );
}
